import { supabase } from '@/lib/supabase';

export const CHANGE_ORDERS_SYNC_EVENT = '__jphph_change_orders_sync__';

export const ARCHIVED_CO_STATUSES = ['cancelled'] as const;

export const CO_NUMBER_DUPLICATE_MESSAGE = 'This CO number already exists for this project.';

export const CO_ATTACHMENT_ACCEPT =
  '.jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf';

export function emitChangeOrdersSync(projectId?: string): void {
  window.dispatchEvent(
    new CustomEvent(CHANGE_ORDERS_SYNC_EVENT, { detail: { projectId } })
  );
}

export function isChangeOrderArchived(status: string): boolean {
  return status === 'cancelled';
}

export function isApprovedChangeOrder(status: string): boolean {
  const normalized = normalizeChangeOrderStatus(status);
  return normalized === 'approved' || normalized === 'client_approved';
}

/** Normalize DB/UI status values (case, spaces, legacy labels). */
export function normalizeChangeOrderStatus(status: string): string {
  return String(status || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

/** Client portal should not see draft or archived change orders. */
// Cancelled remains visible as an audit state; only Draft/Archived are client-hidden.
export const CLIENT_HIDDEN_CO_STATUSES = ['draft', 'archived'] as const;

export function isChangeOrderVisibleToClient(status: string): boolean {
  const normalized = normalizeChangeOrderStatus(status);
  return !CLIENT_HIDDEN_CO_STATUSES.includes(
    normalized as (typeof CLIENT_HIDDEN_CO_STATUSES)[number]
  );
}

export function isChangeOrderRowArchived(row: {
  status?: string | null;
  is_deleted?: boolean | null;
  archived_at?: string | null;
}): boolean {
  if (row.is_deleted === true) return true;
  if (row.archived_at) return true;
  return false;
}

/** Whether a change order row should appear in the client portal. */
export function isClientChangeOrderRecordVisible(co: {
  status: string;
  client_visible?: boolean | null;
  requires_client_approval?: boolean | null;
  internal_only?: boolean | null;
}): boolean {
  const status = normalizeChangeOrderStatus(co.status);

  if (status === 'draft' || status === 'archived') return false;
  if (co.internal_only === true) return false;

  // Approved COs are always visible to the client portal.
  if (status === 'approved' || status === 'client_approved') return true;

  if (co.client_visible === false) return false;

  if (
    status === 'in_progress' &&
    !co.requires_client_approval &&
    co.client_visible !== true
  ) {
    return false;
  }

  return isChangeOrderVisibleToClient(co.status);
}

export type ChangeOrderClientPortalState =
  | 'draft'
  | 'visible'
  | 'hidden'
  | 'auto_visible';

export function getChangeOrderClientPortalState(co: {
  status: string;
  client_visible?: boolean | null;
  requires_client_approval?: boolean | null;
  internal_only?: boolean | null;
}): ChangeOrderClientPortalState {
  const status = normalizeChangeOrderStatus(co.status);
  if (status === 'draft') return 'draft';
  if (!isClientChangeOrderRecordVisible(co)) return 'hidden';
  if (co.client_visible === true || status === 'approved' || status === 'client_approved') {
    return 'visible';
  }
  return 'auto_visible';
}

/** Apply client visibility when status changes on admin save/update. */
export function buildChangeOrderClientVisibilityPatch(status: string): {
  client_visible?: boolean;
} {
  const normalized = normalizeChangeOrderStatus(status);
  if (normalized === 'draft') return { client_visible: false };
  if (
    normalized === 'approved' ||
    normalized === 'client_approved' ||
    normalized === 'pending_client_approval' ||
    normalized === 'sent_to_client' ||
    normalized === 'pending_approval' ||
    normalized === 'pending'
  ) {
    return { client_visible: true };
  }
  return {};
}

export type ChangeOrderAttachmentAudienceFile = {
  photo_type?: string | null;
  is_client_visible?: boolean | null;
};

export function isChangeOrderInvoiceAttachment(file: ChangeOrderAttachmentAudienceFile): boolean {
  return file.photo_type === 'co_invoice';
}

/**
 * Client portal Change Order attachments.
 * photo_type is authoritative: only co_vis_client and co_vis_both.
 * Legacy rows without a co_vis_* photo_type stay client-only when is_client_visible is true
 * (never treated as "both", so they do not become more widely visible).
 */
export function isClientFacingChangeOrderAttachment(file: ChangeOrderAttachmentAudienceFile): boolean {
  if (isChangeOrderInvoiceAttachment(file)) return false;
  const photoType = String(file.photo_type || '');
  if (photoType === 'co_vis_client' || photoType === 'co_vis_both') return true;
  if (photoType === 'co_vis_internal' || photoType === 'co_vis_subcontractor') return false;
  return file.is_client_visible === true;
}

/**
 * Subcontractor portal Change Order attachments.
 * photo_type is authoritative: only co_vis_subcontractor and co_vis_both.
 * is_client_visible must never imply subcontractor visibility.
 */
export function isSubcontractorFacingChangeOrderAttachment(
  file: ChangeOrderAttachmentAudienceFile
): boolean {
  if (isChangeOrderInvoiceAttachment(file)) return false;
  const photoType = String(file.photo_type || '');
  return photoType === 'co_vis_subcontractor' || photoType === 'co_vis_both';
}

/** @deprecated Prefer isClientFacingChangeOrderAttachment — kept for existing client readers. */
export function isClientVisibleAttachment(file: ChangeOrderAttachmentAudienceFile): boolean {
  return isClientFacingChangeOrderAttachment(file);
}

export function isChangeOrderNoPricing(co: {
  builder_cost?: number | null;
  client_price?: number | null;
}): boolean {
  const builder = co.builder_cost ?? 0;
  const client = co.client_price ?? 0;
  return builder === 0 && client === 0;
}

/** No-pricing, non-approved COs can be deleted with a standard confirmation. */
export function canDirectDeleteChangeOrder(co: {
  builder_cost?: number | null;
  client_price?: number | null;
  status: string;
}): boolean {
  return isChangeOrderNoPricing(co) && !isApprovedChangeOrder(co.status);
}

/** Approved or priced COs need a stronger delete confirmation (management only). */
export function requiresDeleteStrongWarning(co: {
  builder_cost?: number | null;
  client_price?: number | null;
  status: string;
}): boolean {
  return !canDirectDeleteChangeOrder(co);
}

/** @deprecated Prefer canDirectDeleteChangeOrder(co) — kept for legacy call sites. */
export function canHardDeleteChangeOrder(status: string): boolean {
  return status === 'draft';
}

const CO_NUMBER_PATTERN = /^CO-(\d+)$/i;

export function normalizeCoNumber(value: string): string {
  const trimmed = value.trim().toUpperCase();
  const match = trimmed.match(/^CO-(\d+)$/);
  if (match) {
    return `CO-${match[1].padStart(3, '0')}`;
  }
  return trimmed;
}

export function formatDefaultCoNumber(seq: number): string {
  return `CO-${String(seq).padStart(3, '0')}`;
}

export function parseCoSequenceFromNumber(coNumber: string): number | null {
  const match = normalizeCoNumber(coNumber).match(CO_NUMBER_PATTERN);
  if (!match) return null;
  const seq = parseInt(match[1], 10);
  return Number.isFinite(seq) ? seq : null;
}

export async function getSuggestedCoNumber(projectId: string): Promise<{ seq: number; number: string }> {
  const { data } = await supabase
    .from('jphph_change_orders')
    .select('co_sequence')
    .eq('project_id', projectId)
    .neq('status', 'cancelled')
    .order('co_sequence', { ascending: false })
    .limit(1);

  const maxSeq = data && data.length > 0 && data[0].co_sequence != null ? data[0].co_sequence : 0;
  const seq = maxSeq + 1;
  return { seq, number: formatDefaultCoNumber(seq) };
}

export async function resolveCoNumberFields(
  projectId: string,
  coNumberInput: string
): Promise<{ co_number: string; co_sequence: number }> {
  const co_number = normalizeCoNumber(coNumberInput);
  const parsed = parseCoSequenceFromNumber(co_number);
  if (parsed != null) {
    return { co_number, co_sequence: parsed };
  }
  const suggested = await getSuggestedCoNumber(projectId);
  return { co_number, co_sequence: suggested.seq };
}

export async function validateCoNumberUnique(
  projectId: string,
  coNumber: string,
  excludeId?: string
): Promise<string | null> {
  const normalized = normalizeCoNumber(coNumber);
  if (!normalized) return 'CO number is required.';

  let query = supabase
    .from('jphph_change_orders')
    .select('id, co_number')
    .eq('project_id', projectId)
    .neq('status', 'cancelled');

  if (excludeId) {
    query = query.neq('id', excludeId);
  }

  const { data, error } = await query;
  if (error) return error.message;

  const duplicate = (data || []).find(
    (row) => normalizeCoNumber(String(row.co_number || '')) === normalized
  );
  if (duplicate) {
    return CO_NUMBER_DUPLICATE_MESSAGE;
  }
  return null;
}

/** Update CO number only — used for approved change orders (Buildertrend renumbering). */
export async function updateChangeOrderCoNumber(params: {
  id: string;
  projectId: string;
  coNumberInput: string;
  previousCoNumber?: string | null;
  userId?: string | null;
}): Promise<{ error?: string; co_number?: string; co_sequence?: number }> {
  const trimmed = params.coNumberInput.trim();
  if (!trimmed) return { error: 'CO number is required.' };

  const duplicateError = await validateCoNumberUnique(params.projectId, trimmed, params.id);
  if (duplicateError) return { error: duplicateError };

  const { co_number, co_sequence } = await resolveCoNumberFields(params.projectId, trimmed);
  const now = new Date().toISOString();

  const { error } = await supabase
    .from('jphph_change_orders')
    .update({ co_number, co_sequence, updated_at: now })
    .eq('id', params.id);

  if (error) return { error: error.message };

  if (import.meta.env.DEV) {
    console.log('[ChangeOrder] CO number updated', {
      id: params.id,
      projectId: params.projectId,
      previousCoNumber: params.previousCoNumber ?? null,
      newCoNumber: co_number,
      userId: params.userId ?? null,
      updated_at: now,
    });
  }

  emitChangeOrdersSync(params.projectId);
  return { co_number, co_sequence };
}

export async function archiveChangeOrder(
  id: string,
  projectId?: string
): Promise<{ error?: string }> {
  const { error } = await supabase
    .from('jphph_change_orders')
    .update({ status: 'cancelled', updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) return { error: error.message };
  emitChangeOrdersSync(projectId);
  return {};
}

/** Soft-delete attachment DB rows linked to a change order (storage files are kept). */
export async function softDeleteChangeOrderAttachments(
  changeOrderId: string
): Promise<{ error?: string }> {
  const { error } = await supabase
    .from('jphph_project_files')
    .update({ is_deleted: true, change_order_id: null })
    .eq('change_order_id', changeOrderId);
  if (error) return { error: error.message };
  return {};
}

/** Permanently delete a change order and detach its attachment records. */
export async function deleteChangeOrder(co: {
  id: string;
  project_id?: string;
}): Promise<{ error?: string }> {
  const attachmentResult = await softDeleteChangeOrderAttachments(co.id);
  if (attachmentResult.error) return attachmentResult;

  const { error } = await supabase
    .from('jphph_change_orders')
    .delete()
    .eq('id', co.id);
  if (error) return { error: error.message };

  emitChangeOrdersSync(co.project_id);
  return {};
}

/** @deprecated Use deleteChangeOrder — always hard-deletes (no archive fallback). */
export async function deleteOrArchiveChangeOrder(co: {
  id: string;
  status: string;
  project_id?: string;
  builder_cost?: number | null;
  client_price?: number | null;
}): Promise<{ error?: string; archived?: boolean }> {
  return deleteChangeOrder(co);
}

/** Soft-delete a change order attachment without removing the parent CO row. */
export async function unlinkChangeOrderAttachment(fileId: string): Promise<void> {
  const { error } = await supabase
    .from('jphph_project_files')
    .update({ is_deleted: true })
    .eq('id', fileId);
  if (error) throw error;
}

export function isChangeOrderFileAllowed(file: File): boolean {
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  const allowed = ['jpg', 'jpeg', 'png', 'webp', 'pdf'];
  if (allowed.includes(ext)) return true;
  if (file.type.startsWith('image/')) return true;
  return file.type === 'application/pdf';
}

export type CoAttachmentVisibility = 'management' | 'client' | 'subcontractor' | 'all';

export const CO_VISIBILITY_PHOTO_TYPES: Record<CoAttachmentVisibility, string> = {
  management: 'co_vis_internal',
  client: 'co_vis_client',
  subcontractor: 'co_vis_subcontractor',
  all: 'co_vis_both',
};

export function coVisibilityFromPhotoType(photoType: string | null | undefined): CoAttachmentVisibility {
  switch (photoType) {
    case 'co_vis_client':
      return 'client';
    case 'co_vis_subcontractor':
      return 'subcontractor';
    case 'co_vis_both':
      return 'all';
    default:
      return 'management';
  }
}

export function coVisibilityLabel(scope: CoAttachmentVisibility): string {
  switch (scope) {
    case 'management':
      return 'Internal only';
    case 'client':
      return 'Visible to client';
    case 'subcontractor':
      return 'Visible to subcontractor';
    case 'all':
      return 'Visible to both';
  }
}

/** Short badge copy for management attachment lists. */
export function coVisibilityBadgeLabel(scope: CoAttachmentVisibility): string {
  switch (scope) {
    case 'management':
      return 'Internal only';
    case 'client':
      return 'Client';
    case 'subcontractor':
      return 'Subcontractor';
    case 'all':
      return 'Client + Subcontractor';
  }
}

export const CO_ATTACHMENT_VISIBILITY_OPTIONS: Array<{
  value: CoAttachmentVisibility;
  label: string;
  badge: string;
}> = [
  { value: 'management', label: 'Internal only', badge: 'Internal only' },
  { value: 'client', label: 'Visible to client', badge: 'Client' },
  { value: 'subcontractor', label: 'Visible to subcontractor', badge: 'Subcontractor' },
  { value: 'all', label: 'Visible to both', badge: 'Client + Subcontractor' },
];

/** Resolve current audience from photo_type, with conservative legacy fallback. */
export function coVisibilityFromAttachment(file: ChangeOrderAttachmentAudienceFile): CoAttachmentVisibility {
  const photoType = String(file.photo_type || '');
  if (photoType.startsWith('co_vis_')) {
    return coVisibilityFromPhotoType(photoType);
  }
  if (file.is_client_visible === true) return 'client';
  return 'management';
}

/** Normalize UI / legacy values to supported attachment visibility scopes. */
export function normalizeCoAttachmentVisibility(value: unknown): CoAttachmentVisibility {
  const raw = String(value ?? '').trim().toLowerCase();
  if (raw === 'management' || raw === 'internal' || raw === 'internal_only' || raw === 'internal only') {
    return 'management';
  }
  if (raw === 'client' || raw === 'visible_client' || raw === 'visible to client') {
    return 'client';
  }
  if (raw === 'subcontractor' || raw === 'visible_subcontractor' || raw === 'visible to subcontractor') {
    return 'subcontractor';
  }
  if (raw === 'all' || raw === 'both' || raw === 'visible_both' || raw === 'visible to both client and subcontractor') {
    return 'all';
  }
  return 'management';
}

export function coAttachmentVisibilityFlags(scope: CoAttachmentVisibility): {
  photo_type: string;
  is_client_visible: boolean;
} {
  const normalized = normalizeCoAttachmentVisibility(scope);
  return {
    photo_type: CO_VISIBILITY_PHOTO_TYPES[normalized],
    is_client_visible: normalized === 'client' || normalized === 'all',
  };
}

export function buildChangeOrderAttachmentInsertPayload(params: {
  projectId: string;
  folderId?: string | null;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  fileType: string;
  note: string | null;
  uploadedBy: string;
  visibility: CoAttachmentVisibility;
  changeOrderId?: string | null;
}): Record<string, unknown> {
  const visibility = normalizeCoAttachmentVisibility(params.visibility);
  const flags = coAttachmentVisibilityFlags(visibility);

  return {
    project_id: params.projectId,
    folder_id: params.folderId ?? null,
    file_name: params.fileName,
    file_url: params.fileUrl,
    file_size: String(params.fileSize),
    file_type: params.fileType,
    note: params.note,
    uploaded_by: params.uploadedBy,
    is_visible: true,
    is_client_visible: flags.is_client_visible,
    is_deleted: false,
    photo_type: flags.photo_type,
    uploaded_context: 'change_order',
    ...(params.changeOrderId ? { change_order_id: params.changeOrderId } : {}),
  };
}

/** Update audience for an existing Change Order attachment without re-uploading. */
export async function updateChangeOrderAttachmentVisibility(
  fileId: string,
  visibility: CoAttachmentVisibility
): Promise<{ error?: string; photo_type?: string; is_client_visible?: boolean }> {
  const flags = coAttachmentVisibilityFlags(visibility);
  const { error } = await supabase
    .from('jphph_project_files')
    .update({
      photo_type: flags.photo_type,
      is_client_visible: flags.is_client_visible,
      updated_at: new Date().toISOString(),
    })
    .eq('id', fileId);

  if (error) {
    if (/updated_at|schema cache|column/i.test(error.message)) {
      const { error: retryError } = await supabase
        .from('jphph_project_files')
        .update({
          photo_type: flags.photo_type,
          is_client_visible: flags.is_client_visible,
        })
        .eq('id', fileId);
      if (retryError) return { error: retryError.message };
      return flags;
    }
    return { error: error.message };
  }

  return flags;
}
